<template>
    <component :is="layoutComponent" v-bind="$props">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-5 flex overflow-hidden moblie_div">
            <!-- No Buyers Error State -->
            <div v-if="chatBuyers.length === 0" class="flex-1 flex flex-col items-center justify-center p-12 text-center bg-gray-50/50 rounded-lg border-2 border-dashed border-gray-200">
                <div class="bg-white p-8 rounded-full shadow-lg mb-8 animate-in zoom-in duration-500">
                    <UserIcon class="h-20 w-20 text-gray-200" />
                </div>
                <h2 class="text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">{{ $t('chat.noBuyersAssigned') }}</h2>
                <p class="text-gray-500 max-w-md mb-10 text-lg leading-relaxed">
                    {{ $t('chat.noBuyersAssignedDesc') }}
                </p>
                <Link 
                    :href="route($page.props.user.type === 'buyer' ? 'clients.apartments.show' : 'advisors.apartments.show', { apartment: apartment.uuid })" 
                    class="px-10 py-4 bg-[#2D3340] hover:bg-gray-800 text-white rounded-2xl shadow-xl transition-all active:scale-95 font-bold text-sm uppercase tracking-widest flex items-center space-x-3"
                >
                    <ChevronDownIcon class="h-5 w-5 rotate-90" />
                    <span>{{ $t('chat.goBackToOverview') }}</span>
                </Link>
            </div>

            <!-- Chat Content -->
            <template v-else>
                <!-- Main Chat Area -->
                <div class="flex-1 flex flex-col min-w-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop">
                <!-- Dropzone Overlay -->
                <div v-if="isDragging" class="absolute inset-0 z-[100] bg-blue-500/10 backdrop-blur-sm border-4 border-blue-500 border-dashed rounded-lg flex items-center justify-center transition-all animate-in fade-in duration-300 pointer-events-none">
                    <div class="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center space-y-4 border border-blue-100 scale-100">
                        <div class="p-4 bg-blue-50 rounded-full">
                            <PlusIcon class="h-12 w-12 text-blue-500" />
                        </div>
                        <div class="text-center">
                            <p class="text-xl font-bold text-gray-900">{{ $t('chat.dropFilesToSend') }}</p>
                            <p class="text-sm text-gray-400 font-medium mt-1 uppercase tracking-widest">{{ $t('chat.releaseToStageFile') }}</p>
                        </div>
                    </div>
                </div>
                <!-- Chat Header -->
                <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white chat-header">
                    <div class="flex items-center space-x-4 text-left">
                        <div class="relative group/avatar">
                            <!-- Original Static Avatar -->
                            <div 
                                class="h-10 w-10 rounded-full bg-cover bg-center shadow-sm"
                                :style="{ backgroundImage: `url(${chatPartner.avatar || 'https://ui-avatars.com/api/?name=' + chatPartner.name + '&background=random'})` }"
                            ></div>
                            <!-- Zooming Preview Avatar -->
                            <img
                                :src="chatPartner.avatar || 'https://ui-avatars.com/api/?name=' + chatPartner.name + '&background=random'"
                                class="absolute inset-0 h-10 w-10 rounded-full object-cover opacity-0 group-hover/avatar:opacity-100 group-hover/avatar:scale-[4] group-hover/avatar:translate-x-10 group-hover/avatar:translate-y-10 group-hover/avatar:rounded-full group-hover/avatar:shadow-2xl z-[60] transition-all duration-300 pointer-events-none origin-top-left"
                                alt="Partner"
                            />
                            <span class="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white" :class="isPartnerOnline ? 'bg-green-400' : 'bg-gray-300'"></span>
                        </div>
                        <div>
                            <h2 class="text-sm font-bold text-gray-900 leading-tight">{{ chatPartner.role === 'Buyers' ? $t('chat.buyers') : (chatPartner.role === 'Buyer' ? $t('chat.buyer') : (chatPartner.role === 'Consultant' ? $t('chat.consultant') : chatPartner.role)) }}: {{ chatPartner.name }}</h2>
                            <p class="text-[10px] font-bold uppercase tracking-widest mt-0.5" :class="isPartnerOnline ? 'text-green-500' : 'text-gray-400'">
                                <template v-if="chatPartner.is_multi">
                                    <template v-if="onlineBuyersCount > 1">
                                        {{ onlineBuyersCount }} {{ $t('chat.online') }}
                                    </template>
                                    <template v-else-if="onlineBuyersCount === 1">
                                        {{ onlineBuyerName }} {{ $t('chat.online') }}
                                    </template>
                                    <template v-else>
                                        {{ $t('chat.offline') }}
                                    </template>
                                </template>
                                <template v-else>
                                    {{ isPartnerOnline ? $t('chat.online') : $t('chat.offline') }}
                                </template>
                                <span v-if="isPartnerTyping" class="text-blue-500 ml-2 animate-pulse normal-case tracking-normal">
                                    {{ typingUserName ? typingUserName + ' ' + $t('chat.isTyping') : $t('chat.typing') }}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <!-- Sidebar Toggle (Chat Media & Files) -->
                        <button
                            @click="toggleSidebar('media')"
                            class="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                            :class="{'bg-gray-100 text-blue-500': activeSidebar === 'media'}"
                            title="Chat Media & Files"
                        >
                            <PhotographIcon class="h-5 w-5" />
                        </button>

                        <!-- Options Dropdown (Mute, Search) -->
                        <div class="relative">
                            <button
                                @click="showOptionsDropdown = !showOptionsDropdown"
                                class="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                                :class="{'bg-gray-100 text-gray-900': showOptionsDropdown}"
                            >
                                <DotsVerticalIcon class="h-5 w-5" />
                            </button>

                            <!-- Options Dropdown Menu -->
                            <div v-if="showOptionsDropdown" class="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                <div class="p-2 space-y-1">
                                    <button
                                        @click="toggleMute(); showOptionsDropdown = false"
                                        class="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 rounded-xl transition-colors group"
                                    >
                                        <div :class="isMuted ? 'text-red-500 bg-red-50' : 'text-gray-400 bg-gray-50'" class="p-2 rounded-lg group-hover:bg-white transition-colors">
                                            <BellIcon class="h-5 w-5" />
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm font-bold text-gray-900">{{ isMuted ? $t('chat.unmuteNotifications') : $t('chat.muteNotifications') }}</p>
                                            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{{ $t('chat.toggleChatAlerts') }}</p>
                                        </div>
                                        <div v-if="isMuted" class="h-2 w-2 rounded-full bg-red-500"></div>
                                    </button>

                                    <button
                                        v-if="$page.props.user.type === 'advisor'"
                                        @click="markAsUnread(); showOptionsDropdown = false"
                                        class="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 rounded-xl transition-colors group"
                                    >
                                        <div class="text-gray-400 bg-gray-50 p-2 rounded-lg group-hover:bg-white transition-colors">
                                            <MailOpenIcon class="h-5 w-5" />
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm font-bold text-gray-900">{{ $t('chat.markAsUnread') }}</p>
                                            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{{ $t('chat.markAsUnreadSubtitle') }}</p>
                                        </div>
                                    </button>

                                    <button
                                        @click="openInChatSearch(); showOptionsDropdown = false"
                                        class="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 rounded-xl transition-colors group lg:hidden"
                                    >
                                        <div class="text-gray-400 bg-gray-50 p-2 rounded-lg group-hover:bg-white transition-colors">
                                            <SearchIcon class="h-5 w-5" />
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm font-bold text-gray-900">{{ $t('chat.searchInChat') }}</p>
                                            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{{ $t('chat.searchInChatDesc') }}</p>
                                        </div>
                                    </button>

                                    <button
                                        @click="toggleSidebar('search'); showOptionsDropdown = false; $nextTick(() => $refs.datePicker.showPicker())"
                                        class="hidden lg:flex w-full text-left px-4 py-3 hover:bg-gray-50 items-center space-x-3 rounded-xl transition-colors group"
                                    >
                                        <div class="text-gray-400 bg-gray-50 p-2 rounded-lg group-hover:bg-white transition-colors">
                                            <CalendarIcon class="h-5 w-5" />
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm font-bold text-gray-900">{{ $t('chat.searchWithDate') }}</p>
                                            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{{ $t('chat.searchWithDateDesc') }}</p>
                                        </div>
                                        <SearchIcon class="h-4 w-4 text-gray-300" />
                                    </button>

                                    <button
                                        v-if="is_advisor"
                                        @click="toggleChatDisabled(); showOptionsDropdown = false"
                                        class="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center space-x-3 rounded-xl transition-colors group"
                                    >
                                        <div :class="thread?.is_disabled ? 'text-orange-500 bg-orange-50' : 'text-gray-400 bg-gray-50'" class="p-2 rounded-lg group-hover:bg-white transition-colors">
                                            <XIcon class="h-5 w-5" />
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm font-bold text-gray-900">{{ thread?.is_disabled ? $t('chat.enableChat') : $t('chat.disableChat') }}</p>
                                            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">{{ $t('chat.toggleChatForBuyers') }}</p>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Messages Area -->
               <div ref="chatContainer" @scroll="handleScroll" class="flex-1 overflow-y-auto p-3 space-y-8 bg-gray-50/30 chat-messages">
                    <!-- Loading More Messages Indicator -->
                    <div v-if="isLoadingMore" class="flex justify-center p-4">
                        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                    <!-- In-chat Search (WhatsApp-style) -->
                    <div
                        v-if="inChatSearchOpen"
                        class="sticky top-0 z-[55] -mx-3 px-3 py-2 bg-white/80 backdrop-blur border-b border-gray-200 lg:hidden"
                    >
                        <div class="flex items-center gap-2">
                            <div class="relative flex-1">
                                <SearchIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    ref="inChatSearchInput"
                                    v-model="inChatSearchQuery"
                                    type="text"
                                    :placeholder="$t('chat.searchInChat')"
                                    translate="no"
                                    class="w-full pl-10 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all placeholder:text-gray-400"
                                    @input="updateInChatSearch({ jumpToFirst: true })"
                                    @keydown.enter.prevent="($event.shiftKey ? prevInChatMatch() : nextInChatMatch())"
                                    @keydown.esc.prevent="closeInChatSearch()"
                                />
                            </div>

                            <div class="flex items-center gap-1.5 flex-shrink-0">
                                <span class="text-[11px] font-bold text-gray-500 tabular-nums min-w-[56px] text-center">
                                    {{ inChatMatchCount === 0 ? '0/0' : `${inChatActiveMatchIndex + 1}/${inChatMatchCount}` }}
                                </span>

                                <div class="relative flex items-center space-x-1">
                                    <div v-if="selectedDate" class="flex items-center bg-blue-50 text-blue-600 px-1.5 py-1 rounded-lg text-[10px] font-bold border border-blue-100">
                                        <button @click="clearDateFilter" class="hover:text-blue-800 transition-colors">
                                            <XIcon class="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div class="relative">
                                        <button
                                            type="button"
                                            class="p-2 bg-white border border-gray-200 rounded-xl text-gray-400 hover:text-blue-600 hover:border-blue-200 transition-all"
                                            :class="{'text-blue-500 border-blue-200 bg-blue-50/30': selectedDate}"
                                            title="Jump to date"
                                            @click="$refs.inChatDatePicker?.showPicker?.()"
                                        >
                                            <CalendarIcon class="h-4 w-4" />
                                        </button>
                                        <input
                                            ref="inChatDatePicker"
                                            type="date"
                                            :max="new Date().toISOString().split('T')[0]"
                                            v-model="selectedDate"
                                            class="absolute inset-0 opacity-0 pointer-events-none"
                                            @change="handleDateChange($event.target.value)"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    class="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:text-gray-800 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    :disabled="inChatMatchCount === 0"
                                    title="Previous match (Shift+Enter)"
                                    @click="prevInChatMatch()"
                                >
                                    <ChevronDownIcon class="h-4 w-4 rotate-180" />
                                </button>
                                <button
                                    type="button"
                                    class="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:text-gray-800 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    :disabled="inChatMatchCount === 0"
                                    title="Next match (Enter)"
                                    @click="nextInChatMatch()"
                                >
                                    <ChevronDownIcon class="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    class="p-2 bg-white border border-gray-200 rounded-xl text-gray-400 hover:text-red-500 hover:border-red-200 transition-all"
                                    title="Close search (Esc)"
                                    @click="closeInChatSearch()"
                                >
                                    <XIcon class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Realtime Messages (DB-backed) -->
                    <div v-if="!realtimeReady" class="flex flex-col items-center justify-center h-full opacity-50 space-y-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <p class="text-sm font-bold text-gray-400">{{ $t('chat.loadingMessages') }}</p>
                    </div>

                    <div v-else-if="messages.length === 0" class="flex flex-col items-center justify-center h-full opacity-50 space-y-4">
                        <div class="p-4 bg-gray-100 rounded-full">
                            <svg class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <p class="text-sm font-bold text-gray-400">{{ $t('chat.noMessagesYet') }}</p>
                    </div>

                    <div v-else class="space-y-6">
                        <div v-for="group in groupedMessages" :key="group.date" class="space-y-3">
                            <!-- Date Separator -->
                            <div :id="'date-' + group.date" class="flex justify-center py-2">
                                <span class="px-4 py-1 bg-gray-200/50 backdrop-blur-sm text-gray-500 text-[10px] font-bold uppercase tracking-widest rounded-full shadow-sm border border-gray-100">
                                    {{ group.readableDate }}
                                </span>
                            </div>

                            <div
                                v-for="m in group.messages"
                                :key="m.id"
                                :id="'msg-' + m.id"
                                class="group relative"
                                :class="[
                                    m.message_type === 'system' ? 'flex justify-center w-full my-4' : 
                                    (Number(m.sender_user_id) === Number($page.props.user.id) ? 'flex flex-col items-end text-left ml-auto' : 'flex space-x-3 text-left mr-auto')
                                ]"
                            >
                                <template v-if="m.message_type === 'system'">
                                     <div class="px-6 py-2 bg-blue-50/50 backdrop-blur-sm border border-blue-100 rounded-2xl flex items-center space-x-3 shadow-sm animate-in zoom-in-95 duration-500">
                                        <div class="h-2 w-2 bg-blue-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(96,165,250,0.6)]"></div>
                                        <span class="text-xs font-bold text-blue-600 tracking-wide" translate="no">{{ m.body }}</span>
                                     </div>
                                </template>
                                <template v-else>
                                    <template v-if="Number(m.sender_user_id) !== Number($page.props.user.id)">
                                        <div
                                            class="relative flex-shrink-0 self-start mt-1"
                                            :class="{ 'cursor-pointer': $page.props.user.type === 'buyer' && m.sender_kind === 'advisor' }"
                                            @click="openAdvisorPopup($event, m)"
                                        >
                                            <img
                                                :src="m.sender_avatar || ('https://ui-avatars.com/api/?name=' + encodeURIComponent(m.sender_name || chatPartner.name))"
                                                class="h-8 w-8 rounded-full shadow-sm object-cover"
                                            />
                                            <span
                                                class="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white"
                                                :class="onlineUserIds.includes(Number(m.sender_user_id)) || onlineUserIds.includes(String(m.sender_user_id)) ? 'bg-green-400' : 'bg-gray-300'"
                                            ></span>
                                        </div>
                                    </template>

                                <div
                                    class="flex flex-col space-y-1 max-w-[85%] md:max-w-[70%] lg:max-w-lg"
                                    :class="Number(m.sender_user_id) === Number($page.props.user.id) ? 'items-end' : 'items-start'"
                                >
                                    <div
                                        v-if="Number(m.sender_user_id) !== Number($page.props.user.id)"
                                        class="text-[10px] font-bold tracking-wide mb-1 text-gray-900 select-none"
                                        :class="{ 'cursor-pointer': $page.props.user.type === 'buyer' && m.sender_kind === 'advisor' }"
                                        @click="openAdvisorPopup($event, m)"
                                    >
                                        {{ m.sender_name }}
                                    </div>
                                    <div class="relative group/msg" :class="m.reactions && m.reactions.length ? 'mb-3' : ''">
                                    <div
                                        class="rounded-2xl shadow-sm text-sm leading-relaxed relative min-w-0"
                                        :class="[
                                            Number(m.sender_user_id) === Number($page.props.user.id) ? 'bg-[#2D3340] text-white rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none border border-gray-200',
                                            (m.attachments && m.attachments.length && !m.body) ? 'p-1.5' : 'px-4 py-3'
                                        ]"
                                    >
                                        <!-- In-Bubble Reply Preview -->
                                        <ChatReplyTo
                                            v-if="m.reply_to_message_id"
                                            :replyMessageId="m.reply_to_message_id"
                                            :replySender="m.reply_to_sender_name || (getMessageById(m.reply_to_message_id) ? (Number(getMessageById(m.reply_to_message_id).sender_user_id) === Number($page.props.user.id) ? $t('chat.you') : (getMessageById(m.reply_to_message_id).sender_name || chatPartner.name)) : (m.reply_to_message ? (Number(m.reply_to_message.sender_user_id) === Number($page.props.user.id) ? $t('chat.you') : (m.reply_to_message.sender_name || chatPartner.name)) : chatPartner.name))"
                                            :replyBody="m.reply_to_message_body || getMessageById(m.reply_to_message_id)?.body || m.reply_to_message?.body || ''"
                                            :replyAttachmentUrl="getMessageById(m.reply_to_message_id)?.attachments?.[0]?.url || m.reply_to_message?.attachments?.[0]?.url"
                                            :replyAttachmentType="getMessageById(m.reply_to_message_id)?.attachments?.[0]?.kind || m.reply_to_message?.attachments?.[0]?.kind || (getMessageById(m.reply_to_message_id)?.attachments?.[0] ? 'document' : null)"
                                            :isOutgoing="Number(m.sender_user_id) === Number($page.props.user.id)"
                                            @scroll-to="scrollToMessage"
                                        />

                                        <div v-if="m.attachments && m.attachments.length" :class="m.body ? 'mb-2' : ''" class="space-y-2">
                                            <div v-for="a in m.attachments" :key="a.id" class="w-full relative">
                                                <ChatPhoto v-if="a.kind === 'photo' || (a.mime_type && a.mime_type.startsWith('image/'))" :attachment="a" @open-lightbox="(url) => openLightbox(url, 'image', a.id)" />
                                                <ChatVideo v-else-if="a.kind === 'video' || (a.mime_type && a.mime_type.startsWith('video/'))" :attachment="a" @play="handleMediaPlay" @open-lightbox="(url) => openLightbox(url, 'video', a.id)" />
                                                <ChatAudio v-else-if="a.kind === 'audio' || (a.mime_type && a.mime_type.startsWith('audio/'))" :attachment="a" :isOutgoing="Number(m.sender_user_id) === Number($page.props.user.id)" @play="handleMediaPlay" @open-lightbox="(url) => openLightbox(url, 'audio', a.id)" />
                                                <ChatDocument v-else :attachment="a" :isOutgoing="Number(m.sender_user_id) === Number($page.props.user.id)" @download="downloadFile" @preview="openDocumentPreview" />
                                            </div>
                                        </div>
                                        <div v-if="m.body" class="whitespace-pre-wrap break-words mt-1">
                                            <template v-if="$page.props.CHAT_MSG_LONG_TEXT_CHAR_MODE === 'On' && m.body.length > ($page.props.chat_msg_long_text_char || 100) && !expandedMessages[m.id]">
                                                <span v-html="formatMessageBody(m.body.substring(0, ($page.props.chat_msg_long_text_char || 100)) + '...')" translate="no"></span>
                                                <button 
                                                    @click="toggleExpand(m.id)" 
                                                    class="text-blue-500 hover:underline font-bold ml-1 text-[11px] uppercase tracking-wider"
                                                >{{ $t('chat.seeMore') }}</button>
                                            </template>
                                            <template v-else>
                                                <span v-html="formatMessageBody(m.body)" translate="no"></span>
                                                <button
                                                    v-if="$page.props.CHAT_MSG_LONG_TEXT_CHAR_MODE === 'On' && m.body.length > ($page.props.chat_msg_long_text_char || 100)"
                                                    @click="toggleExpand(m.id)"
                                                    class="text-blue-500 hover:underline font-bold ml-1 text-[11px] uppercase tracking-wider"
                                                >{{ expandedMessages[m.id] ? $t('chat.seeLess') : $t('chat.seeMore') }}</button>
                                            </template>
                                            <span v-if="m.edited_at" class="text-[9px] text-gray-300 font-bold ml-1 italic opacity-70">({{ $t('chat.edited') }})</span>
                                        </div>
                                        <!-- Reactions -->
                                        <ChatReaction
                                            :reactions="m.reactions"
                                            :isOutgoing="Number(m.sender_user_id) === Number($page.props.user.id)"
                                            @show-details="showReactionSummary(m)"
                                        />
                                    </div>
                                    <div class="absolute top-1 right-2 opacity-0 group-hover/msg:opacity-100 transition-all z-20">
                                        <div class="relative">
                                            <button
                                                @click="toggleMsgMenu(m.id)"
                                                class="p-1 rounded-full transition-all"
                                                :class="Number(m.sender_user_id) === Number($page.props.user.id) ? 'text-white/70 hover:text-white bg-black/20 hover:bg-black/40' : 'text-gray-400 hover:text-gray-600 bg-gray-100/80 hover:bg-gray-200'"
                                            >
                                                <ChevronDownIcon class="h-4 w-4" />
                                            </button>
                                            <MessageMenu
                                                v-if="activeMsgMenu === m.id"
                                                :messageId="m.id"
                                                :messageText="m.body || (m.attachments?.[0]?.original_name || '')"
                                                :senderName="Number(m.sender_user_id) === Number($page.props.user.id) ? $t('chat.you') : (m.sender_name || chatPartner.name)"
                                                :isOutgoing="Number(m.sender_user_id) === Number($page.props.user.id)"
                                                :isFile="!!(m.attachments && m.attachments.length)"
                                                :fileName="m.attachments?.[0]?.original_name"
                                                :sentAt="m.sent_at"
                                                :canEdit="m.can_edit"
                                                @info="showMessageInfo"
                                                @reply="() => setReply({ 
                                                    id: m.id, 
                                                    sender: (Number(m.sender_user_id) === Number($page.props.user.id) ? $t('chat.you') : (m.sender_name || chatPartner.name)),
                                                    text: m.body || '',
                                                    attachmentUrl: m.attachments?.[0]?.url,
                                                    attachmentType: m.attachments?.[0]?.kind || (m.attachments?.[0] ? 'document' : null)
                                                })"
                                                @copy="copyMessage"
                                                @download="downloadFile(m)"
                                                @downloadFile="downloadFile"
                                                @react="({ id, emoji }) => reactToMessage(id, emoji)"
                                                @openEmojiPicker="openEmojiPickerForReaction"
                                                @edit="startEdit"
                                            />
                                        </div>
                                    </div>
                                    </div>
                                    <div class="text-[9px] text-gray-400 uppercase font-bold px-1 flex items-center justify-end space-x-1" :class="{'mt-1': m.reactions && m.reactions.length}">
                                        <span>{{ formatTime(m.sent_at) }}</span>
                                        <template v-if="Number(m.sender_user_id) === Number($page.props.user.id)">
                                            <!-- Double Blue Tick (Read) -->
                                            <div v-if="m.id && thread && Number(thread.partner_last_read_message_id) >= Number(m.id)" class="flex items-center ml-1">
                                                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" class="text-blue-500">
                                                    <path d="M2 12l5 5L17 7" />
                                                    <path d="M8 12l5 5L22 7" />
                                                </svg>
                                            </div>
                                            <!-- Double Grey Tick (Sent/Delivered) -->
                                            <div v-else-if="m.id" class="flex items-center ml-1">
                                                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" class="text-gray-400">
                                                    <path d="M2 12l5 5L17 7" />
                                                    <path d="M8 12l5 5L22 7" />
                                                </svg>
                                            </div>
                                            <!-- Single Grey Tick (Pending) -->
                                            <div v-else class="flex items-center ml-1">
                                                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none" class="text-gray-400">
                                                    <path d="M5 12l5 5L20 7" />
                                                </svg>
                                            </div>
                                        </template>
                                    </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>


                </div>
                
                <!-- Scroll to Bottom Button -->
                <transition
                    enter-active-class="transition duration-300 ease-out"
                    enter-from-class="translate-y-10 opacity-0 scale-90"
                    enter-to-class="translate-y-0 opacity-100 scale-100"
                    leave-active-class="transition duration-200 ease-in"
                    leave-from-class="translate-y-0 opacity-100 scale-100"
                    leave-to-class="translate-y-10 opacity-0 scale-90"
                >
                    <button
                        v-if="showScrollToBottom"
                        @click="scrollToBottom('smooth')"
                        class="absolute bottom-24 right-8 z-[50] p-3 bg-white text-blue-500 rounded-full shadow-2xl border border-gray-100 hover:bg-blue-50 transition-all active:scale-90 group ring-4 ring-blue-500/5 mb-2"
                        title="Scroll to bottom"
                    >
                        <ChevronDownIcon class="h-6 w-6 group-hover:translate-y-0.5 transition-transform" />
                    </button>
                </transition>

                <!-- Chat Input Area -->
                <div v-if="canSendMessages" class="p-4 bg-white border-t border-gray-200 chat-input-area">
                    <!-- Reply Area -->
                    <div v-if="replyingTo" class="mb-4 flex items-center p-3 bg-gray-50 rounded-xl border-l-4 border-blue-500 animate-in slide-in-from-bottom-2">
                        <div class="flex-1 min-w-0 pr-4">
                            <p class="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{{ $t('chat.replyingTo') }} {{ replyingTo.sender }}</p>
                            <p class="text-xs text-gray-600 truncate mt-0.5 italic flex items-center">
                                <template v-if="!replyingTo.text && replyingTo.attachmentType">
                                    <PhotographIcon v-if="replyingTo.attachmentType === 'photo' || replyingTo.attachmentType === 'image'" class="h-3 w-3 inline mr-1" />
                                    <VideoCameraIcon v-else-if="replyingTo.attachmentType === 'video'" class="h-3 w-3 inline mr-1" />
                                    <MicrophoneIcon v-else-if="replyingTo.attachmentType === 'audio'" class="h-3 w-3 inline mr-1" />
                                    <DocumentTextIcon v-else class="h-3 w-3 inline mr-1" />
                                    <span>{{ formatAttachmentType(replyingTo.attachmentType) }}</span>
                                </template>
                                <template v-else>
                                    "{{ replyingTo.text }}"
                                </template>
                            </p>
                        </div>

                        <!-- Attachment Preview in Compose Area -->
                        <div v-if="replyingTo.attachmentUrl || replyingTo.attachmentType" class="w-12 h-12 flex-shrink-0 bg-gray-200/50 flex items-center justify-center rounded-lg overflow-hidden ml-2 border border-gray-100">
                            <template v-if="replyingTo.attachmentType === 'photo' || replyingTo.attachmentType === 'image'">
                                <img :src="replyingTo.attachmentUrl" class="w-full h-full object-cover" />
                            </template>
                            <template v-else-if="replyingTo.attachmentType === 'video'">
                                <div class="w-full h-full bg-gray-800 flex items-center justify-center">
                                    <PlayIcon class="h-6 w-6 text-white/80" />
                                </div>
                            </template>
                            <template v-else-if="replyingTo.attachmentType === 'audio'">
                                <MicrophoneIcon class="h-5 w-5 text-gray-400" />
                            </template>
                            <template v-else>
                                <DocumentTextIcon class="h-5 w-5 text-gray-400" />
                            </template>
                        </div>

                        <button @click="clearReply" class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors ml-2">
                            <XIcon class="h-4 w-4" />
                        </button>
                    </div>

                    <!-- Edit Area -->
                    <div v-if="editingMessageId" class="mb-4 flex items-center p-3 bg-amber-50 rounded-xl border-l-4 border-amber-500 animate-in slide-in-from-bottom-2">
                        <div class="flex-1 min-w-0 pr-4">
                            <p class="text-[10px] font-bold text-amber-600 uppercase tracking-widest">{{ $t('chat.editingMessage') }}</p>
                            <p class="text-xs text-gray-600 truncate mt-0.5 italic">"{{ getMessageById(editingMessageId)?.body }}"</p>
                        </div>
                        <button @click="cancelEdit" class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors ml-2">
                            <XIcon class="h-4 w-4" />
                        </button>
                    </div>

                    <!-- File Preview Overlay (WhatsApp Style) -->
                    <ChatPreviewOverlay 
                        :showOverlay="showPreviewOverlay"
                        :files="filesToUpload"
                        :selectedIndex="selectedFileIndex"
                        :isSending="isSending"
                        @update:selectedIndex="selectedFileIndex = $event"
                        @remove-file="removeFile"
                        @trigger-add="triggerFileInput"
                        @send-files="sendFiles"
                        @update-caption="(idx, cap) => filesToUpload[idx].caption = cap"
                        @update-file="(idx, file) => filesToUpload[idx].file = file"
                        @paste-files="handleFiles"
                        @close="closePreviewOverlay(false)"
                    />

                    <div class="flex items-center space-x-4 relative">
                        <!-- Attachment Menu (Hidden when recording or audio pending) -->
                        <div v-if="!isRecording && !recordedAudio" class="relative">
                            <button 
                                @click="showPlusMenu = !showPlusMenu"
                                class="p-3 bg-gray-100 text-gray-500 rounded-full hover:bg-gray-200 transition-all active:scale-90"
                                :class="{'bg-gray-800 text-white': showPlusMenu}"
                            >
                                <PlusIcon class="h-5 w-5 transition-transform" :class="{'rotate-45': showPlusMenu}" />
                            </button>
                            
                            <!-- Plus Options Menu -->
                            <div class="relative">
                                <!-- Hidden Inputs (Moved out of v-if to be accessible from overlay) -->
                                <input type="file" ref="docInput" class="hidden" multiple accept="*/*" @change="handleFileSelect" />
                                <input type="file" ref="photoInput" class="hidden" multiple accept="image/*" @change="handleFileSelect" />
                                <input type="file" ref="videoInput" class="hidden" multiple accept="video/*" @change="handleFileSelect" />
                                <input type="file" ref="cameraInput" class="hidden" accept="image/*" capture="camera" @change="handleFileSelect" />
                                <input type="file" ref="audioInput" class="hidden" multiple accept="audio/*" @change="handleFileSelect" />
                                <input type="file" ref="contactInput" class="hidden" multiple accept=".vcf" @change="handleFileSelect" />

                                <div v-if="showPlusMenu" class="absolute bottom-full mb-4 left-0 w-56 bg-white border border-gray-200 rounded-2xl shadow-2xl p-2 z-[70] animate-in slide-in-from-bottom-2 duration-200">
                                    <button @click="$refs.docInput.click()" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-blue-50 rounded-xl transition-colors text-left group">
                                        <DocumentTextIcon class="h-5 w-5 text-blue-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-blue-600">{{ $t('chat.document') }}</span>
                                    </button>
                                    <button @click="$refs.photoInput.click()" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-purple-50 rounded-xl transition-colors text-left group">
                                        <PhotographIcon class="h-5 w-5 text-purple-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-purple-600">{{ $t('chat.photos') }}</span>
                                    </button>
                                    <button @click="$refs.videoInput.click()" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 rounded-xl transition-colors text-left group">
                                        <VideoCameraIcon class="h-5 w-5 text-red-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-red-600">{{ $t('chat.videos') }}</span>
                                    </button>
                                    <button @click="openWebcam" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-emerald-50 rounded-xl transition-colors text-left group">
                                        <CameraIcon class="h-5 w-5 text-emerald-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-emerald-600">{{ $t('chat.camera') }}</span>
                                    </button>
                                    <button @click="$refs.audioInput.click()" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 rounded-xl transition-colors text-left group">
                                        <MicrophoneIcon class="h-5 w-5 text-orange-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-orange-600">{{ $t('chat.audio') }}</span>
                                    </button>
                                    <!-- <button @click="$refs.contactInput.click()" class="w-full flex items-center space-x-3 px-4 py-3 hover:bg-indigo-50 rounded-xl transition-colors text-left group">
                                        <UserIcon class="h-5 w-5 text-indigo-500" />
                                        <span class="text-sm font-semibold text-gray-700 group-hover:text-indigo-600">Contact</span>
                                    </button> -->
                            </div>
                        </div>
                    </div>

                        <!-- Main Input Area / Recording UI / Audio Preview -->
                        <div class="flex-1 relative text-left min-h-[48px] flex items-center">
                            <!-- Recording Timer Overlay -->
                            <div v-if="isRecording" class="absolute inset-0 bg-white z-20 flex items-center justify-between px-4 animate-in slide-in-from-left-2 rounded-full border border-red-100 shadow-sm">
                                <div class="flex items-center space-x-3">
                                    <div class="h-3 w-3 bg-red-500 rounded-full animate-pulse border-2 border-red-200"></div>
                                    <span class="text-sm font-bold text-gray-700 tracking-tight">{{ recordingTime }}</span>
                                </div>
                                <div class="flex items-center space-x-4">
                                    <button @click="cancelRecording" class="text-xs font-bold text-gray-400 hover:text-red-500 uppercase tracking-widest transition-colors">{{ $t('chat.cancel') }}</button>
                                    
                                    <!-- Pause/Resume Recording -->
                                    <button v-if="!isRecordingPaused" @click="pauseVoiceRecord" class="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                                        <PauseIcon class="h-6 w-6" />
                                    </button>
                                    <button v-else @click="resumeVoiceRecord" class="p-1.5 text-red-500 hover:text-red-600 transition-colors animate-pulse">
                                        <PlayIcon class="h-6 w-6" />
                                    </button>

                                    <button @click="stopRecording" class="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all shadow-md shadow-red-200">
                                        <div class="h-4 w-4 bg-white rounded-sm"></div>
                                    </button>
                                </div>
                            </div>

                            <!-- Audio Player Preview (After Stop) -->
                            <div v-else-if="recordedAudio" class="absolute inset-0 bg-gray-50 z-20 flex items-center space-x-4 px-4 animate-in zoom-in-95 rounded-full border border-gray-200 shadow-inner">
                                <button @click="cancelRecording" class="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                    <TrashIcon class="h-5 w-5" />
                                </button>
                                <div class="flex-1 h-8 flex items-center justify-center space-x-3 bg-white px-4 rounded-full border border-gray-100">
                                    <button @click="toggleRecordedAudio" class="text-gray-600 hover:text-blue-500 transition-colors">
                                        <PlayIcon v-if="!isPlayingAudio" class="h-5 w-5" />
                                        <PauseIcon v-else class="h-5 w-5" />
                                    </button>
                                    <div @click="seekAudio" class="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden relative cursor-pointer group/progress">
                                        <!-- Progress Fill -->
                                        <div :style="{ width: audioProgress + '%' }" class="absolute inset-y-0 left-0 bg-blue-500 transition-all duration-100"></div>
                                        <!-- Interactive Handle -->
                                        <div :style="{ left: audioProgress + '%' }" class="absolute top-1/2 -translate-y-1/2 h-4 w-1 bg-blue-600 rounded-full shadow-md group-hover:h-5 transition-all"></div>
                                    </div>
                                    <span class="text-[10px] font-bold text-gray-400 tabular-nums">
                                        {{ formatAudioTime(audioCurrentTime) }} / {{ formatAudioTime(audioDuration) }}
                                    </span>
                                </div>
                            </div>

                            <div v-else class="flex-1 min-w-0 relative">
                                <!-- Floating Formatting Toolbar -->
                                <transition
                                    enter-active-class="transition duration-200 ease-out"
                                    enter-from-class="opacity-0 translate-y-2 scale-95"
                                    enter-to-class="opacity-100 translate-y-0 scale-100"
                                    leave-active-class="transition duration-150 ease-in"
                                    leave-from-class="opacity-100 translate-y-0 scale-100"
                                    leave-to-class="opacity-0 translate-y-2 scale-95"
                                >
                                    <div 
                                        v-if="showFormattingToolbar" 
                                        class="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 flex items-center bg-[#1c1c1c] text-white px-2 py-1.5 rounded-xl shadow-2xl z-[100] border border-white/10 space-x-1 min-w-max"
                                    >
                                        <button @click="applyFormatting('*')" class="p-2 hover:bg-white/10 rounded-lg transition-colors group" title="Bold">
                                            <span class="font-bold w-5 h-5 flex items-center justify-center text-sm">B</span>
                                        </button>
                                        <button @click="applyFormatting('_')" class="p-2 hover:bg-white/10 rounded-lg transition-colors group" title="Italic">
                                            <span class="italic w-5 h-5 flex items-center justify-center font-serif text-sm">I</span>
                                        </button>
                                        <button @click="applyFormatting('~')" class="p-2 hover:bg-white/10 rounded-lg transition-colors group" title="Strikethrough">
                                            <span class="line-through w-5 h-5 flex items-center justify-center text-sm">S</span>
                                        </button>
                                        <button @click="applyFormatting('__')" class="p-2 hover:bg-white/10 rounded-lg transition-colors group" title="Underline">
                                            <span class="underline w-5 h-5 flex items-center justify-center text-sm">U</span>
                                        </button>
                                        <div class="w-px h-4 bg-white/20 mx-1"></div>
                                        <button @click="applyFormatting('`')" class="p-2 hover:bg-white/10 rounded-lg transition-colors group" title="Code">
                                            <CodeIcon class="h-4 w-4" />
                                        </button>
                                    </div>
                                </transition>

                                <div
                                    ref="messageInput"
                                    contenteditable="true"
                                    class="w-full bg-gray-50 border border-gray-200 rounded-3xl px-5 py-3 text-sm focus:ring-4 focus:ring-gray-500/10 focus:bg-white focus:border-gray-400 transition-all placeholder:text-gray-400 pr-12 shadow-sm resize-none custom-scrollbar min-h-[48px] max-h-32 overflow-y-auto outline-none break-words"
                                    :data-placeholder="newMessage ? '' : $t('chat.typeMessage')"
                                    translate="no"
                                    @input="handleInput"
                                    @keydown="handleKeyDown"
                                    @keydown.enter.exact.prevent="sendMessage"
                                    @keydown.alt.enter.exact.prevent="insertNewline"
                                    @keydown.shift.enter.exact.prevent="insertNewline"
                                    @paste="onPaste"
                                    @select="checkSelection"
                                    @mouseup="checkSelection"
                                    @keyup="checkSelection"
                                ></div>
                            </div>

                            <!-- Emoji Picker Toggle -->
                            <div v-if="!isRecording && !recordedAudio" class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                                <button 
                                    @click="showEmojiPicker = !showEmojiPicker"
                                    class="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-all focus:outline-none">
                                    <EmojiHappyIcon class="h-5 w-5" />
                                </button>
                                
                                <!-- Emoji Picker Popover -->
                                <div v-if="showEmojiPicker" class="absolute bottom-full right-0 mb-4 z-[70] animate-in fade-in zoom-in-95 duration-200 emojis_box">
                                    <EmojiPicker 
                                        :native="true" 
                                        theme="light"
                                        :static-texts="{ placeholder: 'Search emoji...' }"
                                        @select="onSelectEmoji" 
                                        class="emoji-picker-container shadow-2xl border border-gray-200 rounded-2xl"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex items-center space-x-2">
                             <button 
                                v-if="newMessage || recordedAudio || selectedFiles.length > 0"
                                @click="recordedAudio ? sendVoiceMessage() : sendMessage()"
                                :disabled="isSending"
                                class="bg-[#2D3340] text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-gray-700 transition-all shadow-lg shadow-gray-200 flex items-center space-x-2 active:scale-95 animate-in slide-in-from-right-2 disabled:opacity-50"
                            >
                                <template v-if="!isSending">
                                    <span v-if="recordedAudio">{{ $t('chat.sendVoice') }}</span>
                                    <span v-else>{{ $t('chat.send') }}</span>
                                    <PaperAirplaneIcon class="h-4 w-4 rotate-90" />
                                </template>
                                <div v-else class="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            </button>
                            <button 
                                v-else-if="!isRecording"
                                @click="startVoiceRecord"
                                class="bg-[#2D3340] text-white p-3.5 rounded-full hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 flex items-center justify-center active:scale-95 animate-in zoom-in-95"
                            >
                                <MicrophoneIcon class="h-6 w-6" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Disabled Chat Message -->
                <div v-else class="p-8 text-center bg-gray-50 border-t border-gray-200 chat-input-area">
                    <div class="flex flex-col items-center space-y-3 opacity-60">
                        <XIcon class="h-10 w-10 text-orange-500" />
                        <p class="text-sm font-bold text-gray-900">{{ $t('chat.chatDisabled') }}</p>
                        <p v-if="is_advisor" class="text-xs text-gray-500">{{ $t('chat.enableChatToTalk') }}</p>
                    </div>
                </div>
            </div>

            <!-- Right Sidebar: Shared Media & Files / Search -->
            <transition
                enter-active-class="transition ease-out duration-300 transform"
                enter-from-class="translate-x-full"
                enter-to-class="translate-x-0"
                leave-active-class="transition ease-in duration-300 transform"
                leave-from-class="translate-x-0"
                leave-to-class="translate-x-full"
            >
                <div
                    v-if="activeSidebar"
                    class="fixed inset-y-0 right-0 z-50 w-80 bg-white shadow-2xl lg:relative lg:z-0 lg:ml-6 lg:shadow-none flex-shrink-0"
                >
                    <!-- Media Sidebar -->
                    <div v-if="activeSidebar === 'media'" class="h-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <!-- Sidebar Header with Tabs -->
                        <div class="border-b border-gray-200 bg-white">
                            <div class="px-6 py-4 flex items-center justify-between">
                                <button @click="activeSidebar = null" class="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                                    <XIcon class="h-5 w-5" />
                                </button>
                                <h3 class="text-sm font-bold text-gray-900 uppercase tracking-tight">{{ $t('chat.sharedContent') }}</h3>
                                <div class="w-7"></div> <!-- Spacer for centering title -->
                            </div>
                            
                            <!-- WhatsApp Style Tabs -->
                            <div class="flex px-4">
                                <button 
                                    v-for="tab in ['media', 'docs', 'links']" 
                                    :key="tab"
                                    @click="mediaTab = tab"
                                    class="flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-all relative"
                                    :class="mediaTab === tab ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600'"
                                >
                                    {{ $t('chat.' + tab) }}
                                    <div v-if="mediaTab === tab" class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 animate-in fade-in zoom-in-95 duration-200"></div>
                                </button>
                            </div>
                        </div>

                        <div class="flex-1 overflow-y-auto p-4 custom-scrollbar">
                            <!-- Shared Media Tab -->
                            <div v-if="mediaTab === 'media'" class="space-y-6">
                                <div v-if="Object.keys(sharedMedia.groupedMedia).length === 0" class="flex flex-col items-center justify-center py-20 text-center opacity-40">
                                    <PhotographIcon class="h-12 w-12 text-gray-300 mb-3" />
                                    <p class="text-xs font-bold text-gray-500">{{ $t('chat.noMedia') }}</p>
                                </div>
                                <div v-for="(items, label) in sharedMedia.groupedMedia" :key="label" class="text-left">
                                    <h4 class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 sticky top-0 bg-white/80 backdrop-blur py-1 z-10">{{ label }}</h4>
                                    <div class="grid grid-cols-3 gap-2">
                                        <div 
                                            v-for="(item, idx) in sharedMedia.groupedMedia[label]" 
                                            :key="item.id" 
                                            class="relative aspect-square group cursor-pointer overflow-hidden rounded-lg bg-gray-100"
                                            @click="openLightbox(item.url, item.type, item.id)"
                                        >
                                            <img v-if="item.kind === 'photo'" :src="item.url" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                            <div v-else-if="item.kind === 'video'" class="w-full h-full bg-gray-800 flex items-center justify-center relative">
                                                <VideoCameraIcon class="h-6 w-6 text-white/50" />
                                                <div class="absolute bottom-1 right-1 bg-black/50 px-1 rounded text-[8px] text-white font-bold tabular-nums">Video</div>
                                            </div>
                                            <div v-else-if="item.kind === 'audio'" class="w-full h-full bg-blue-50 flex items-center justify-center relative">
                                                <MicrophoneIcon class="h-6 w-6 text-blue-400" />
                                                <div class="absolute bottom-1 right-1 bg-blue-500/10 px-1 rounded text-[8px] text-blue-600 font-bold tabular-nums">Audio</div>
                                            </div>
                                            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Shared Documents Tab -->
                            <div v-if="mediaTab === 'docs'" class="space-y-3">
                                <div v-if="sharedMedia.docs.length === 0" class="flex flex-col items-center justify-center py-20 text-center opacity-40">
                                    <DocumentTextIcon class="h-12 w-12 text-gray-300 mb-3" />
                                    <p class="text-xs font-bold text-gray-500">{{ $t('chat.noDocs') }}</p>
                                </div>
                                <div 
                                    v-for="doc in sharedMedia.docs" 
                                    :key="doc.id"
                                    class="flex items-center justify-between p-3 border border-gray-100 rounded-2xl hover:bg-gray-50 transition-all cursor-pointer group hover:shadow-sm"
                                    @click="scrollToMessage(doc.messageId)"
                                >
                                    <div class="flex items-center space-x-3 text-left min-w-0 flex-1">
                                        <div class="bg-red-50 p-2 rounded-xl group-hover:bg-red-100 transition-colors">
                                            <DocumentTextIcon class="h-5 w-5 text-red-500" />
                                        </div>
                                        <div class="min-w-0">
                                            <p class="text-xs font-bold text-gray-900 truncate">{{ doc.original_name }}</p>
                                            <p class="text-[9px] text-gray-400 font-bold uppercase tracking-tighter mt-0.5">
                                                {{ doc.extension?.toUpperCase() || 'DOC' }} • {{ formatFileSize(doc.size) }} • {{ formatDateSimple(doc.sent_at) }}
                                            </p>
                                        </div>
                                    </div>
                                    <button 
                                        @click.stop="downloadFile(doc)"
                                        class="ml-2 bg-gray-50 p-1.5 rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                    >
                                        <DownloadIcon class="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            <!-- Shared Links Tab -->
                            <div v-if="mediaTab === 'links'" class="space-y-4">
                                <div v-if="sharedMedia.links.length === 0" class="flex flex-col items-center justify-center py-20 text-center opacity-40">
                                    <LinkIcon class="h-12 w-12 text-gray-300 mb-3" />
                                    <p class="text-xs font-bold text-gray-500">{{ $t('chat.noLinks') }}</p>
                                </div>
                                <div 
                                    v-for="link in sharedMedia.links" 
                                    :key="link.id"
                                    class="p-4 border border-gray-100 rounded-2xl hover:bg-blue-50/30 transition-all cursor-pointer group text-left relative overflow-hidden"
                                    @click="scrollToMessage(link.id)"
                                >
                                    <div class="flex items-center justify-between mb-2">
                                        <span class="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{{ link.sender_name }}</span>
                                        <span class="text-[9px] text-gray-400 font-bold">{{ formatDateSimple(link.sent_at) }}</span>
                                    </div>
                                    
                                    <!-- Simple Link Preview Style -->
                                    <div class="bg-gray-50 rounded-xl p-3 border border-gray-100 group-hover:bg-white transition-colors">
                                        <p class="text-xs text-gray-600 line-clamp-3 leading-relaxed break-words" v-html="formatMessageBody(link.body)"></p>
                                    </div>
                                    
                                    <!-- Forward Arrow (Jump to) -->
                                    <div class="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ChevronDownIcon class="h-4 w-4 text-gray-300 -rotate-90" />
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Load More Shared Content -->
                            <div v-if="sharedContent.hasMore[mediaTab]" class="pt-4 text-center">
                                <button 
                                    @click="loadSharedContent()" 
                                    :disabled="isLoadingShared"
                                    class="text-xs font-bold text-blue-500 hover:text-blue-600 uppercase tracking-widest disabled:opacity-50"
                                >
                                    {{ isLoadingShared ? $t('chat.loading') : $t('chat.loadMore') }}
                                </button>
                            </div>
                        </div>


                    </div>

                    <!-- Search Sidebar -->
                    <div v-if="activeSidebar === 'search'" class="h-full flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
                            <h3 class="text-sm font-bold text-gray-900 uppercase tracking-tight">{{ $t('chat.searchMessages') }}</h3>
                            <button
                                @click="activeSidebar = null"
                                class="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            >
                                <XIcon class="h-5 w-5" />
                            </button>
                        </div>

                        <div class="p-4 bg-gray-50/50 border-b border-gray-100">
                            <div class="relative flex items-center space-x-2">
                                <div class="relative flex-1">
                                    <SearchIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input 
                                        v-model="searchQuery"
                                        type="text" 
                                        :placeholder="$t('chat.searchPlaceholder')"
                                        translate="no"
                                        class="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all placeholder:text-gray-400"
                                        @input="handleSearch"
                                    />
                                </div>
                                <div class="relative flex items-center space-x-1">
                                    <div v-if="selectedDate" class="flex items-center bg-blue-50/80 backdrop-blur-sm text-blue-600 px-3 py-1.5 rounded-xl text-[11px] font-bold border border-blue-100/50 shadow-sm animate-in fade-in zoom-in-95 duration-300">
                                        <CalendarIcon class="h-3.5 w-3.5 mr-1.5 opacity-70" />
                                        <span>{{ selectedDate }}</span>
                                        <button @click="clearDateFilter" class="ml-2 p-0.5 hover:bg-blue-100 rounded-full transition-all text-blue-400 hover:text-blue-600">
                                            <XIcon class="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div class="relative flex-shrink-0">
                                        <div class="date-picker-wrapper">
                                            <CalendarIcon class="calendar-icon h-5 w-5" />
                                            <input 
                                                type="date" 
                                                :max="new Date().toISOString().split('T')[0]"
                                                v-model="selectedDate"
                                                @change="handleDateChange($event.target.value)"
                                                class="native-date-input"
                                                title="Jump to date"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                            <div v-if="!searchQuery" class="flex flex-col items-center justify-center h-full text-center space-y-4 px-6 opacity-50">
                                <div class="p-4 bg-gray-100 rounded-full">
                                    <SearchIcon class="h-8 w-8 text-gray-400" />
                                </div>
                                <div>
                                    <p class="text-sm font-bold text-gray-900">{{ $t('chat.searchMessages') }}</p>
                                    <p class="text-xs text-gray-500 mt-1">{{ $t('chat.searchDescription') }}</p>
                                </div>
                            </div>

                            <div v-else-if="searchResults.length === 0" class="flex flex-col items-center justify-center h-full text-center space-y-4 px-6 opacity-50">
                                <p class="text-sm font-bold text-gray-900">{{ $t('chat.noSearchResults') }}</p>
                                <p class="text-xs text-gray-500">{{ $t('chat.tryDifferentKeywords') }}</p>
                            </div>

                            <div v-else class="space-y-1">
                                <button
                                    v-for="result in searchResults"
                                    :key="result.id"
                                    @click="scrollToMessage(result.id)"
                                    class="w-full text-left p-3 hover:bg-blue-50 rounded-xl transition-all border border-transparent hover:border-blue-100 group"
                                >
                                    <div class="flex items-center justify-between mb-1">
                                        <span class="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{{ result.sender }}</span>
                                        <span class="text-[10px] text-gray-400 font-medium">{{ formatDateLabel(result.date) }}, {{ formatTime(result.date) }}</span>
                                    </div>
                                    <p class="text-xs text-gray-700 line-clamp-2 leading-relaxed" v-html="highlightText(result.text, searchQuery)"></p>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Overlay for mobile/sidebar -->
            <transition
                enter-active-class="transition-opacity duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-300"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-if="activeSidebar"
                    @click="activeSidebar = null"
                    class="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
                ></div>
            </transition>
            
            <!-- Image Lightbox (Fancybox Style) -->
            <transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div 
                    v-if="lightboxUrl" 
                    ref="lightboxContainer"
                    class="fixed inset-0 z-[120] bg-black/95 backdrop-blur-2xl flex flex-col items-center justify-between outline-none" 
                    @keydown.left="prevLightboxItem" 
                    @keydown.right="nextLightboxItem" 
                    @keydown.esc="lightboxUrl = null" 
                    tabindex="0"
                >
                    <!-- Close Button -->
                    <button @click="lightboxUrl = null" class="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all z-[130] group">
                        <XIcon class="h-8 w-8 group-hover:rotate-90 transition-transform" />
                    </button>
                    
                    <!-- Navigation Arrows -->
                    <button 
                        v-if="lightboxActiveIndex > 0"
                        @click="prevLightboxItem" 
                        class="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all z-[130] group hover:scale-110 active:scale-95 shadow-2xl"
                    >
                        <ChevronDownIcon class="h-10 w-10 rotate-90" />
                    </button>

                    <button 
                        v-if="lightboxActiveIndex < lightboxItems.length - 1"
                        @click="nextLightboxItem" 
                        class="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-white/5 hover:bg-white/10 text-white rounded-full transition-all z-[130] group hover:scale-110 active:scale-95 shadow-2xl"
                    >
                        <ChevronDownIcon class="h-10 w-10 -rotate-90" />
                    </button>

                    <!-- Main Content Area -->
                    <div class="flex-1 w-full flex items-center justify-center p-4 md:p-8 relative min-h-0 overflow-hidden">
                        <transition
                            enter-active-class="transition duration-300 ease-out"
                            enter-from-class="opacity-0 translate-y-4"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition duration-200 ease-in"
                            leave-from-class="opacity-100 translate-y-0"
                            leave-to-class="opacity-0 -translate-y-4"
                            mode="out-in"
                        >
                            <div :key="lightboxUrl" class="h-full w-full flex flex-col items-center justify-center">
                                <img 
                                    v-if="lightboxType === 'image'"
                                    :src="lightboxUrl" 
                                    class="max-h-[50vh] max-w-full object-contain rounded-xl shadow-[0_0_80px_rgba(0,0,0,0.5)] cursor-zoom-out" 
                                    @click="lightboxUrl = null"
                                />
                                <video 
                                    v-else-if="lightboxType === 'video'"
                                    :src="lightboxUrl"
                                    controls
                                    class="max-h-[50vh] max-w-full rounded-xl shadow-[0_0_80px_rgba(0,0,0,0.5)]"
                                    @click.stop
                                ></video>
                                <audio 
                                    v-else-if="lightboxType === 'audio'"
                                    :src="lightboxUrl"
                                    controls
                                    class="w-full max-w-md mx-auto"
                                    @click.stop
                                ></audio>
                                
                                <!-- Meta/Caption Info -->
                                <div v-if="lightboxActiveIndex >= 0" class="mt-6 text-center max-w-2xl px-4">
                                    <p class="text-[10px] font-bold text-blue-400 uppercase tracking-[4px] mb-2">{{ lightboxActiveIndex + 1 }} / {{ lightboxItems.length }}</p>
                                    <p class="text-white font-medium text-lg mb-1">{{ lightboxItems[lightboxActiveIndex].original_name }}</p>
                                    <p class="text-white/50 text-xs italic">{{ lightboxItems[lightboxActiveIndex].body || $t('chat.sharedInProject') }}</p>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Bottom Controller Area -->
                    <div class="w-full bg-black/90 backdrop-blur-3xl border-t border-white/10 p-4 md:p-5 flex flex-col items-center space-y-4 md:space-y-5 z-[130]">
                        <!-- Thumbnails (Hidden on mobile for space) -->
                        <div class="hidden md:flex items-center justify-center space-x-3 overflow-x-auto custom-scrollbar max-w-full px-4 py-2">
                            <div 
                                v-for="(item, idx) in lightboxItems" 
                                :key="item.id"
                                @click="selectLightboxItem(idx)"
                                class="h-12 w-16 flex-shrink-0 cursor-pointer p-0.5 rounded-lg transition-all active:scale-95"
                                :class="lightboxActiveIndex === idx ? 'ring-2 ring-emerald-500 scale-110 shadow-[0_0_20px_rgba(16,185,129,0.3)] z-10' : 'opacity-40 hover:opacity-100 hover:scale-105'"
                            >
                                <img v-if="item.type === 'image'" :src="item.url" class="h-full w-full object-cover rounded-md" />
                                <div v-else-if="item.type === 'video'" class="h-full w-full bg-gray-800 rounded-md flex items-center justify-center">
                                    <VideoCameraIcon class="h-4 w-4 text-white/50" />
                                </div>
                                <div v-else-if="item.type === 'audio'" class="h-full w-full bg-blue-900 rounded-md flex items-center justify-center">
                                    <MicrophoneIcon class="h-4 w-4 text-white/50" />
                                </div>
                                <div v-else class="h-full w-full bg-gray-800 rounded-md flex items-center justify-center">
                                    <DocumentTextIcon class="h-4 w-4 text-white/50" />
                                </div>
                            </div>
                        </div>

                        <!-- Actions Area (Always visible) -->
                        <div class="flex items-center space-x-3 w-full justify-center md:w-auto">
                            <button 
                                @click="scrollToMessage(lightboxItems[lightboxActiveIndex].messageId); lightboxUrl = null"
                                class="flex-1 md:flex-none flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-2xl shadow-2xl transition-all active:scale-95 font-bold text-[10px] md:text-xs uppercase tracking-widest whitespace-nowrap"
                            >
                                <ReplyIcon class="h-4 w-4" />
                                <span>{{ $t('chat.goToMessage') }}</span>
                            </button>
                            <button 
                                @click="downloadFile(lightboxItems[lightboxActiveIndex])"
                                class="flex-1 md:flex-none flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-2xl shadow-2xl transition-all active:scale-95 font-bold text-[10px] md:text-xs uppercase tracking-widest whitespace-nowrap"
                            >
                                <DownloadIcon class="h-4 w-4" />
                                <span>{{ $t('actions.download') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </transition>
            <!-- Webcam Modal -->
            <transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="opacity-0 scale-95"
                enter-to-class="opacity-100 scale-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="opacity-100 scale-100"
                leave-to-class="opacity-0 scale-95"
            >
                <div v-if="showWebcamModal" class="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div class="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-2xl w-full flex flex-col relative animate-in zoom-in-95 duration-300">
                        <!-- Close Button -->
                        <button @click="closeWebcam" class="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/20 text-gray-800 rounded-full transition-all z-20">
                            <XIcon class="h-6 w-6" />
                        </button>

                        <div class="p-6 border-b border-gray-100">
                            <h3 class="text-lg font-bold text-gray-900">{{ $t('chat.takePhoto') }}</h3>
                            <p class="text-xs text-gray-400 font-medium uppercase tracking-widest mt-1">{{ $t('chat.captureWebcam') }}</p>
                        </div>

                        <div class="flex-1 bg-gray-900 relative min-h-[400px] flex items-center justify-center">
                            <!-- Live Stream -->
                            <video 
                                v-show="!capturedImage"
                                ref="webcamVideo" 
                                autoplay 
                                playsinline 
                                class="w-full h-full object-cover"
                                :class="{'opacity-50': webcamError}"
                                style="transform: scaleX(-1);"
                            ></video>
                            
                            <!-- Preview captured image -->
                            <img 
                                v-if="capturedImage"
                                :src="capturedImage" 
                                class="w-full h-full object-cover" 
                            />

                            <!-- Error Message -->
                            <div v-if="webcamError" class="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-white">
                                <VideoCameraIcon class="h-12 w-12 text-white/20 mb-4" />
                                <p class="text-sm font-bold">{{ webcamError }}</p>
                                <button @click="openWebcam" class="mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-xs font-bold uppercase tracking-widest transition-all">{{ $t('chat.retake') }}</button>
                            </div>
                        </div>

                        <!-- Controls -->
                        <div class="p-6 bg-white border-t border-gray-100 flex items-center justify-center space-x-4">
                            <template v-if="!capturedImage">
                                <button 
                                    @click="closeWebcam" 
                                    class="px-6 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 uppercase tracking-widest transition-all"
                                >{{ $t('chat.cancel') }}</button>
                                <button 
                                    @click="takePhoto" 
                                    :disabled="!webcamStream || webcamError"
                                    class="h-16 w-16 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-blue-200 transition-all active:scale-90 disabled:opacity-50 disabled:grayscale"
                                >
                                    <div class="h-12 w-12 border-4 border-white rounded-full"></div>
                                </button>
                                <div class="w-20"></div> <!-- Spacer for centering -->
                            </template>
                            <template v-else>
                                <button
                                    @click="retakePhoto"
                                    class="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl transition-all active:scale-95 font-bold text-xs uppercase tracking-widest"
                                >
                                    <RefreshIcon class="h-4 w-4" />
                                    <span>{{ $t('chat.retake') }}</span>
                                </button>
                                <button
                                    @click="confirmCapturedPhoto"
                                    class="flex items-center space-x-2 px-8 py-3 bg-[#2D3340] hover:bg-gray-700 text-white rounded-2xl shadow-xl transition-all active:scale-95 font-bold text-xs uppercase tracking-widest"
                                >
                                    <CheckIcon class="h-5 w-5" />
                                    <span>{{ $t('chat.confirmUpload') }}</span>
                                </button>
                            </template>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Message Info Modal -->
            <MessageInfoModal
                :show="showMsgInfoModal"
                :message="msgInfo.message"
                :seenBy="msgInfo.seen_by"
                :isLoading="msgInfo.isLoading"
                @close="closeMsgInfoModal"
            />

            <!-- Reaction Summary Modal -->
            <ReactionModal
                :show="showReactionModal"
                :reactions="activeReactionMessage?.reactions || []"
                @close="closeReactionModal"
                @react="(emoji) => { reactToMessage(activeReactionMessage.id, emoji); }"
                @remove="(emoji) => { reactToMessage(activeReactionMessage.id, emoji); }"
            />

            <!-- Discard Confirmation Modal -->
            <DiscardConfirmationModal
                :show="showDiscardModal"
                @confirm="confirmDiscardFiles"
                @cancel="showDiscardModal = false"
            />

            <!-- File Preview Modal -->
            <FilePreviewModal
                :show="!!previewFile"
                :fileUrl="previewFile ? previewFile.url : ''"
                :fileName="previewFile ? previewFile.name : ''"
                @close="previewFile = null"
            />

            <ChatAdvisorPopup
                :show="!!activeAdvisorPopup"
                :senderName="activeAdvisorPopup ? activeAdvisorPopup.senderName : ''"
                :senderAvatar="activeAdvisorPopup ? activeAdvisorPopup.senderAvatar : ''"
                :senderEmail="activeAdvisorPopup ? activeAdvisorPopup.senderEmail : ''"
                :senderMobile="activeAdvisorPopup ? activeAdvisorPopup.senderMobile : null"
                :senderKind="activeAdvisorPopup ? activeAdvisorPopup.senderKind : 'advisor'"
                :top="activeAdvisorPopup ? activeAdvisorPopup.top : 0"
                :left="activeAdvisorPopup ? activeAdvisorPopup.left : 0"
                @close="activeAdvisorPopup = null"
            />
            </template>
        </div>
    </component>
</template>

<script src="./chat.js"></script>

<style scoped src="./chat.css"></style>